import { FOLDER_MAX_DEPTH, FOLDER_NAME_MAX_LENGTH, type Folder } from '@kanto/shared';
import { useState, type ReactElement } from 'react';
import { useT } from '../lib/i18n';

/**
 * The tree the notes are filed into, built from the flat list the server sends.
 *
 * **The inbox comes first, and it is not a folder.** It is `folderId === null` —
 * every note that has not been given a place yet — and it sits at the top
 * because that is where quick capture puts things and therefore where the next
 * thing to do is (D260815b).
 *
 * The counts are of the notes already on screen, not asked for: the whole list
 * arrives in one query, so the number beside a folder is a `filter` over what is
 * in hand rather than a second round trip per row.
 *
 * The tree is drawn from a list the server already sorted by path, so a child
 * always follows its parent: the indentation is computed by walking up to the
 * root, and nothing here re-orders anything.
 */

export interface FolderTreeProps {
  folders: Folder[];
  /** Notes per folder, `null` keyed for the inbox. */
  counts: Map<string | null, number>;
  selected: string | null | undefined;
  onSelect: (folderId: string | null | undefined) => void;
  onCreate: (name: string, parentId: string | null) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  busy: boolean;
}

export function FolderTree({
  folders,
  counts,
  selected,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  busy,
}: FolderTreeProps): ReactElement {
  const t = useT();
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);

  // How far in each folder is drawn. Bounded rather than trusted to terminate:
  // the server refuses a cycle, and a render is not where one should be found.
  const depths = new Map<string, number>();
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  for (const folder of folders) {
    let depth = 0;
    let current: Folder | undefined = folder;
    while (current && depth <= FOLDER_MAX_DEPTH) {
      depth += 1;
      current = current.parentId === null ? undefined : byId.get(current.parentId);
    }
    depths.set(folder.id, depth - 1);
  }

  // A new folder lands beside whatever is selected rather than always at the
  // root: somebody who has just opened a folder and pressed "New folder" meant
  // one inside it.
  const parentForNew = typeof selected === 'string' ? selected : null;

  return (
    <nav aria-label={t('notes.folders')} className="text-sm">
      <ul>
        <li>
          <Row
            label={t('notes.everything')}
            count={total(counts)}
            depth={0}
            selected={selected === undefined}
            onSelect={() => onSelect(undefined)}
          />
        </li>
        <li>
          <Row
            label={t('notes.inbox')}
            count={counts.get(null) ?? 0}
            depth={0}
            selected={selected === null}
            onSelect={() => onSelect(null)}
          />
        </li>

        {folders.map((folder) => (
          <li key={folder.id}>
            {renaming === folder.id ? (
              <NameField
                initial={folder.name}
                depth={depths.get(folder.id) ?? 0}
                onCancel={() => setRenaming(null)}
                onCommit={(name) => {
                  setRenaming(null);
                  if (name !== folder.name) onRename(folder.id, name);
                }}
              />
            ) : (
              <Row
                label={folder.name}
                count={counts.get(folder.id) ?? 0}
                depth={depths.get(folder.id) ?? 0}
                selected={selected === folder.id}
                onSelect={() => onSelect(folder.id)}
                onRename={() => setRenaming(folder.id)}
                onDelete={() => onDelete(folder.id)}
                renameLabel={t('notes.renameFolder', folder.name)}
                deleteLabel={t('notes.deleteFolder', folder.name)}
              />
            )}
          </li>
        ))}
      </ul>

      {adding ? (
        <NameField
          initial=""
          depth={typeof selected === 'string' ? (depths.get(selected) ?? 0) + 1 : 0}
          onCancel={() => setAdding(false)}
          onCommit={(name) => {
            setAdding(false);
            onCreate(name, parentForNew);
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          disabled={busy}
          className="mt-1 flex min-h-9 w-full items-center gap-2 rounded-lg px-2 text-sm text-ink-500 hover:bg-surface hover:text-ink-900 disabled:opacity-60"
        >
          <svg
            viewBox="0 0 24 24"
            className="size-4 shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          {t('notes.newFolder')}
        </button>
      )}
    </nav>
  );
}

function total(counts: Map<string | null, number>): number {
  let sum = 0;
  for (const count of counts.values()) sum += count;
  return sum;
}

/**
 * One line of the tree.
 *
 * The two actions appear on hover and on `focus-within`, as they do on a todo
 * row — a column where every line carries two permanent buttons is a column
 * where the buttons are what you read.
 */
function Row({
  label,
  count,
  depth,
  selected,
  onSelect,
  onRename,
  onDelete,
  renameLabel,
  deleteLabel,
}: {
  label: string;
  count: number;
  depth: number;
  selected: boolean;
  onSelect: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  renameLabel?: string;
  deleteLabel?: string;
}): ReactElement {
  return (
    <div className="group flex items-center gap-1">
      <button
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        style={{ paddingLeft: `${0.5 + depth * 0.75}rem` }}
        className={`flex min-h-9 min-w-0 flex-1 items-center gap-2 rounded-lg pr-2 text-left transition-colors ${
          selected ? 'bg-accent-soft font-medium text-accent' : 'text-ink-700 hover:bg-surface'
        }`}
      >
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <span className="shrink-0 text-xs tabular-nums opacity-60">{count}</span>
      </button>

      {onRename && onDelete && (
        <span className="flex shrink-0 items-center opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
          <button
            type="button"
            onClick={onRename}
            aria-label={renameLabel}
            className="flex size-7 items-center justify-center rounded-lg text-ink-400 hover:text-ink-900"
          >
            <svg
              viewBox="0 0 24 24"
              className="size-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 20h4L19 9a2 2 0 0 0-3-3L5 17v3Z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onDelete}
            aria-label={deleteLabel}
            className="flex size-7 items-center justify-center rounded-lg text-ink-400 hover:text-alert"
          >
            <svg
              viewBox="0 0 24 24"
              className="size-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M4 7h16M9 7V5h6v2M6 7v13a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7" />
            </svg>
          </button>
        </span>
      )}
    </div>
  );
}

/** The inline field that names a folder, whether it is new or being renamed. */
function NameField({
  initial,
  depth,
  onCommit,
  onCancel,
}: {
  initial: string;
  depth: number;
  onCommit: (name: string) => void;
  onCancel: () => void;
}): ReactElement {
  const t = useT();
  const [value, setValue] = useState(initial);

  const commit = (): void => {
    const name = value.trim();
    // An empty name is refused by the server, and a folder with none could not
    // be found again anyway.
    if (name === '') onCancel();
    else onCommit(name);
  };

  return (
    <input
      type="text"
      value={value}
      autoFocus
      maxLength={FOLDER_NAME_MAX_LENGTH}
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        // Emptied first, so that a blur arriving with the unmount commits
        // nothing — `commit` reads an empty name as "never mind".
        if (event.key === 'Escape') {
          setValue('');
          onCancel();
        }
      }}
      aria-label={t('notes.folderName')}
      style={{ marginLeft: `${depth * 0.75}rem` }}
      className="min-h-9 w-full rounded-lg border border-accent bg-paper px-2 text-sm outline-none"
    />
  );
}
